import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, ImagePlus } from "lucide-react";
import { DashShell, Kpi, StatusPill } from "@/components/DashShell";
import { useAuth } from "@/auth";
import { WhatsappButton } from "@/components/WhatsappButton";
import { useLang } from "@/i18n";
import { api, fmtErr, inr, API } from "@/api";
import { PLANS_META, QR_PAYMENT } from "@/constants";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const inputCls =
  "w-full border border-[#E5E2D9] bg-white px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200";
const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2";

export default function CustomerDashboard() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [passbook, setPassbook] = useState({ entries: [], balance: 0 });
  const [invForm, setInvForm] = useState({ plan_type: "daily_recurring", amount: "", reference_id: "", name: "", phone: "", bank_name: "", account_holder: "", account_number: "", ifsc: "" });
  const [invProofFile, setInvProofFile] = useState(null);
  const [invProofPreview, setInvProofPreview] = useState(null);
  const invFileRef = useRef(null);
  const [depForm, setDepForm] = useState({ amount: "", reference_id: "", note: "" });
  const [wdForm, setWdForm] = useState({ amount: "", note: "", bank_name: "", account_holder: "", account_number: "", ifsc: "" });
  const [loanData, setLoanData] = useState({ eligible: 0, used: 0, remaining: 0, loans: [] });
  const [loanForm, setLoanForm] = useState({ amount: "", note: "" });
  const [emiPay, setEmiPay] = useState(null);
  const [emiRef, setEmiRef] = useState("");
  const [emiProofFile, setEmiProofFile] = useState(null);
  const emiFileRef = useRef(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [payment, setPayment] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [s, inv, pb, pay, ln] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/investments/my"),
        api.get("/passbook"),
        api.get("/settings/payment"),
        api.get("/loans"),
      ]);
      setSummary(s.data);
      setInvestments(inv.data);
      setPassbook(pb.data);
      setPayment(pay.data);
      setLoanData(ln.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (user?.bank_details) setWdForm((f) => ({ ...f, ...user.bank_details }));
    if (user) {
      setInvForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        phone: f.phone || user.phone || "",
        ...(user.bank_details
          ? Object.fromEntries(Object.entries(user.bank_details).filter(([k, v]) => v && !f[k]))
          : {}),
      }));
    }
  }, [user]);

  const act = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const pickProof = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProofFile(f);
    setProofPreview(URL.createObjectURL(f));
  };

  const pickInvProof = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setInvProofFile(f);
    setInvProofPreview(URL.createObjectURL(f));
  };

  const submitInvestment = () =>
    act("inv", async () => {
      const prof = await api.put("/profile", {
        name: invForm.name, phone: invForm.phone,
        bank_name: invForm.bank_name, account_holder: invForm.account_holder,
        account_number: invForm.account_number, ifsc: invForm.ifsc,
      });
      setUser(prof.data);
      const inv = await api.post("/investments", { plan_type: invForm.plan_type, amount: Number(invForm.amount) });
      const fd = new FormData();
      fd.append("file", invProofFile);
      const up = await api.post("/deposits/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/deposits", {
        amount: Number(invForm.amount), investment_id: inv.data.id,
        reference_id: invForm.reference_id.trim(), proof_path: up.data.path, note: "Investment payment",
      });
      setInvForm((f) => ({ ...f, amount: "", reference_id: "" }));
      setInvProofFile(null);
      setInvProofPreview(null);
    }, t("msg_inv_submitted"));

  const submitDeposit = () =>
    act("dep", async () => {
      const fd = new FormData();
      fd.append("file", proofFile);
      const up = await api.post("/deposits/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const proof_path = up.data.path;
      await api.post("/deposits", { amount: Number(depForm.amount), reference_id: depForm.reference_id.trim(), note: depForm.note || null, proof_path });
      setDepForm({ amount: "", reference_id: "", note: "" });
      setProofFile(null);
      setProofPreview(null);
    }, t("msg_dep_submitted"));

  const submitEmi = (loanId, n) =>
    act("emi", async () => {
      const fd = new FormData();
      fd.append("file", emiProofFile);
      const up = await api.post("/deposits/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/loans/${loanId}/emis/${n}/pay`, { reference_id: emiRef.trim(), proof_path: up.data.path });
      setEmiPay(null);
      setEmiRef("");
      setEmiProofFile(null);
    }, t("msg_emi_submitted"));

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/passbook/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "matrix-works-passbook.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const planName = (id) => {
    const meta = PLANS_META.find((p) => p.id === id);
    return meta ? t(meta.nameKey) : id;
  };

  const chartData = (() => {
    const byDate = {};
    [...passbook.entries].reverse().forEach((e) => {
      byDate[e.entry_date] = e.balance;
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, balance]) => ({ date: d.slice(5), balance }));
  })();

  const PaymentBox = ({ testid }) => (
    <div className="mt-4 border border-[#E5E2D9] bg-[#FDFCF8] p-4 text-center" data-testid={testid}>
      <img src={payment?.qr_path ? `${API}/settings/qr` : QR_PAYMENT.url} alt="Matrix Works UPI QR — Scan and Pay" className="h-44 mx-auto object-contain" />
      <div className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-[#B5952F]">{t("qr_title")}</div>
      <div className="text-xs text-[#5C5C5C] mt-1 tabular">{t("qr_upi")}: {payment?.upi_id || QR_PAYMENT.upi}</div>
      {(payment?.bank_name || payment?.account_number) && (
        <div className="mt-3 pt-3 border-t border-[#E5E2D9] text-left" data-testid={`${testid}-bank`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B5952F] mb-2">{t("d_bank_details")}</div>
          {payment.account_name && <div className="text-xs text-[#1A1A1A] font-semibold">{payment.account_name}</div>}
          {payment.bank_name && <div className="text-xs text-[#5C5C5C]">{payment.bank_name}</div>}
          {payment.account_number && <div className="text-xs text-[#5C5C5C] tabular">A/C: {payment.account_number}</div>}
          {payment.ifsc && <div className="text-xs text-[#5C5C5C] tabular">IFSC: {payment.ifsc}</div>}
        </div>
      )}
      <div className="text-[11px] text-[#5C5C5C] mt-2">{t("qr_hint")}</div>
    </div>
  );

  return (
    <DashShell title={t("nav_dashboard")} subtitle={t("d_dash_sub")} testid="customer-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="customer-kpi-strip">
        <Kpi label={t("d_balance")} value={inr(summary?.balance)} gold testid="kpi-balance" />
        <Kpi label={t("d_invested")} value={inr(summary?.invested)} testid="kpi-invested" />
        <Kpi label={t("d_returns")} value={`+${inr(summary?.returns)}`} testid="kpi-returns" />
        <Kpi label={t("d_active")} value={summary?.active_plans ?? 0} testid="kpi-active-plans" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-[#E5E2D9] p-7" data-testid="new-investment-card">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("d_new_investment")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t("d_full_name")}</label>
              <input value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} className={inputCls} data-testid="investment-name-input" />
            </div>
            <div>
              <label className={labelCls}>{t("d_mobile")}</label>
              <input value={invForm.phone} onChange={(e) => setInvForm({ ...invForm, phone: e.target.value })} className={inputCls} data-testid="investment-mobile-input" />
            </div>
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_email")}</label>
            <input value={user?.email || ""} readOnly className={`${inputCls} opacity-60 cursor-not-allowed`} data-testid="investment-email-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_select_plan")}</label>
            <select
              value={invForm.plan_type}
              onChange={(e) => setInvForm({ ...invForm, plan_type: e.target.value })}
              className={inputCls}
              data-testid="investment-plan-select"
            >
              {PLANS_META.map((p) => (
                <option key={p.id} value={p.id}>{t(p.nameKey)}</option>
              ))}
            </select>
          </div>
          <div className="mt-4">
            <label className={labelCls}>
              {invForm.plan_type === "daily_recurring" ? t("calc_daily_deposit") : t("d_amount")} (₹)
            </label>
            <input type="number" min="1" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} className={inputCls} placeholder="5000" data-testid="investment-amount-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_bank_details")}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={invForm.account_holder} onChange={(e) => setInvForm({ ...invForm, account_holder: e.target.value })} className={inputCls} placeholder={t("d_ac_holder")} data-testid="investment-account-holder-input" />
              <input value={invForm.bank_name} onChange={(e) => setInvForm({ ...invForm, bank_name: e.target.value })} className={inputCls} placeholder={t("d_bank_name")} data-testid="investment-bank-name-input" />
              <input value={invForm.account_number} onChange={(e) => setInvForm({ ...invForm, account_number: e.target.value })} className={inputCls} placeholder={t("d_ac_number")} data-testid="investment-account-number-input" />
              <input value={invForm.ifsc} onChange={(e) => setInvForm({ ...invForm, ifsc: e.target.value.toUpperCase() })} className={inputCls} placeholder={t("d_ifsc")} data-testid="investment-ifsc-input" />
            </div>
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_reference_id")} *</label>
            <input value={invForm.reference_id} onChange={(e) => setInvForm({ ...invForm, reference_id: e.target.value })} className={inputCls} placeholder="e.g. 432198765432" data-testid="investment-reference-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_proof")}</label>
            <input ref={invFileRef} type="file" accept="image/*" onChange={pickInvProof} className="hidden" data-testid="investment-proof-input" />
            <button
              type="button"
              onClick={() => invFileRef.current?.click()}
              className="w-full border border-dashed border-[#D4AF37] bg-[#FDFCF8] px-4 py-3 text-sm font-semibold text-[#5C5C5C] hover:bg-[#F7F0D8] transition-colors duration-200 flex items-center justify-center gap-2"
              data-testid="investment-proof-button"
            >
              <ImagePlus size={16} className="text-[#B5952F]" />
              {invProofFile ? invProofFile.name : t("d_proof_choose")}
            </button>
            {invProofPreview && (
              <img src={invProofPreview} alt="Payment proof preview" className="mt-3 h-24 mx-auto object-contain border border-[#E5E2D9]" data-testid="investment-proof-preview" />
            )}
          </div>
          <PaymentBox testid="investment-qr-box" />
          <button
            onClick={submitInvestment}
            disabled={busy === "inv" || !invForm.amount || !invForm.name.trim() || !invForm.phone.trim() || !invForm.account_number.trim() || !invForm.ifsc.trim() || !invForm.reference_id.trim() || !invProofFile}
            className="mt-5 w-full bg-[#1A1A1A] text-[#D4AF37] py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
            data-testid="investment-submit-button"
          >
            {t("d_submit")}
          </button>
          <WhatsappButton
            label={t("wa_invest")}
            message={`Hello Matrix Works, I want to invest ${invForm.amount ? `Rs.${invForm.amount}` : ""} in the ${t(PLANS_META.find((p) => p.id === invForm.plan_type)?.nameKey)} plan. Please guide me.`}
            testid="investment-whatsapp-button"
            className="mt-3 w-full"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="bg-white border border-[#E5E2D9] p-7" data-testid="deposit-card">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("d_add_deposit")}</h3>
          <label className={labelCls}>{t("d_amount")} (₹)</label>
          <input type="number" min="1" value={depForm.amount} onChange={(e) => setDepForm({ ...depForm, amount: e.target.value })} className={inputCls} placeholder="1000" data-testid="deposit-amount-input" />
          <div className="mt-4">
            <label className={labelCls}>{t("d_reference_id")} *</label>
            <input value={depForm.reference_id} onChange={(e) => setDepForm({ ...depForm, reference_id: e.target.value })} className={inputCls} placeholder="e.g. 432198765432" data-testid="deposit-reference-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_note")}</label>
            <input value={depForm.note} onChange={(e) => setDepForm({ ...depForm, note: e.target.value })} className={inputCls} placeholder="UPI / Cash / Bank" data-testid="deposit-note-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_proof")}</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickProof} className="hidden" data-testid="deposit-proof-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-[#D4AF37] bg-[#FDFCF8] px-4 py-3 text-sm font-semibold text-[#5C5C5C] hover:bg-[#F7F0D8] transition-colors duration-200 flex items-center justify-center gap-2"
              data-testid="deposit-proof-button"
            >
              <ImagePlus size={16} className="text-[#B5952F]" />
              {proofFile ? proofFile.name : t("d_proof_choose")}
            </button>
            {proofPreview && (
              <img src={proofPreview} alt="Payment proof preview" className="mt-3 h-24 mx-auto object-contain border border-[#E5E2D9]" data-testid="deposit-proof-preview" />
            )}
          </div>
          <PaymentBox testid="upi-qr-box" />
          <button
            onClick={submitDeposit}
            disabled={busy === "dep" || !depForm.amount || !depForm.reference_id.trim() || !proofFile}
            className="mt-5 w-full bg-[#D4AF37] text-[#1A1A1A] py-3.5 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50"
            data-testid="deposit-submit-button"
          >
            {t("d_add_deposit")}
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="bg-white border border-[#E5E2D9] p-7" data-testid="withdrawal-card">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("d_withdraw")}</h3>
          <label className={labelCls}>{t("d_amount")} (₹)</label>
          <input type="number" min="1" value={wdForm.amount} onChange={(e) => setWdForm({ ...wdForm, amount: e.target.value })} className={inputCls} placeholder="500" data-testid="withdrawal-amount-input" />
          <div className="mt-4">
            <label className={labelCls}>{t("d_bank_details")}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={wdForm.account_holder} onChange={(e) => setWdForm({ ...wdForm, account_holder: e.target.value })} className={inputCls} placeholder={t("d_ac_holder")} data-testid="withdrawal-account-holder-input" />
              <input value={wdForm.bank_name} onChange={(e) => setWdForm({ ...wdForm, bank_name: e.target.value })} className={inputCls} placeholder={t("d_bank_name")} data-testid="withdrawal-bank-name-input" />
              <input value={wdForm.account_number} onChange={(e) => setWdForm({ ...wdForm, account_number: e.target.value })} className={inputCls} placeholder={t("d_ac_number")} data-testid="withdrawal-account-number-input" />
              <input value={wdForm.ifsc} onChange={(e) => setWdForm({ ...wdForm, ifsc: e.target.value.toUpperCase() })} className={inputCls} placeholder={t("d_ifsc")} data-testid="withdrawal-ifsc-input" />
            </div>
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("d_note")}</label>
            <input value={wdForm.note} onChange={(e) => setWdForm({ ...wdForm, note: e.target.value })} className={inputCls} data-testid="withdrawal-note-input" />
          </div>
          <button
            onClick={() =>
              act("wd", async () => {
                await api.post("/withdrawals", {
                  amount: Number(wdForm.amount),
                  note: wdForm.note || null,
                  bank_name: wdForm.bank_name || null,
                  account_holder: wdForm.account_holder || null,
                  account_number: wdForm.account_number || null,
                  ifsc: wdForm.ifsc || null,
                });
                setWdForm((f) => ({ ...f, amount: "", note: "" }));
              }, t("msg_wd_submitted"))
            }
            disabled={busy === "wd" || !wdForm.amount || !wdForm.account_number || !wdForm.ifsc}
            className="mt-5 w-full border-2 border-[#1A1A1A] text-[#1A1A1A] py-3.5 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50"
            data-testid="withdrawal-submit-button"
          >
            {t("d_withdraw")}
          </button>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-12" data-testid="loan-section">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="bg-[#1A1A1A] p-7 noise-overlay" data-testid="loan-apply-card">
          <h3 className="font-display text-xl font-bold text-white mb-1">{t("d_loan_title")}</h3>
          <p className="text-xs text-white/50 mb-5">{t("p6_tag")}</p>
          {loanData.eligible <= 0 ? (
            <p className="text-sm text-white/70 leading-relaxed" data-testid="loan-need-plan">{t("d_loan_need_plan")}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-px bg-[#3A3A3A] border border-[#3A3A3A] mb-5">
                <div className="bg-[#232323] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("d_loan_eligible")}</div>
                  <div className="font-display text-2xl font-bold text-white tabular mt-1" data-testid="loan-eligible">{inr(loanData.eligible)}</div>
                </div>
                <div className="bg-[#232323] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B5952F]">{t("d_loan_available")}</div>
                  <div className="font-display text-2xl font-bold text-[#D4AF37] tabular mt-1" data-testid="loan-remaining">{inr(loanData.remaining)}</div>
                </div>
              </div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/60 mb-2">{t("d_amount")} (₹)</label>
              <input type="number" min="1" value={loanForm.amount} onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })} className="w-full border border-[#3A3A3A] bg-[#232323] text-white px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200" placeholder="50000" data-testid="loan-amount-input" />
              {loanForm.amount && Number(loanForm.amount) > loanData.remaining && (
                <p className="text-xs text-[#FF6B6B] font-semibold mt-2" data-testid="loan-over-error">
                  {t("d_loan_available")}: {inr(loanData.remaining)}
                </p>
              )}
              <div className="mt-4">
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/60 mb-2">{t("d_note")}</label>
                <input value={loanForm.note} onChange={(e) => setLoanForm({ ...loanForm, note: e.target.value })} className="w-full border border-[#3A3A3A] bg-[#232323] text-white px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200" data-testid="loan-note-input" />
              </div>
              <button
                onClick={() =>
                  act("loan", async () => {
                    await api.post("/loans", { amount: Number(loanForm.amount), note: loanForm.note || null });
                    setLoanForm({ amount: "", note: "" });
                  }, t("msg_loan_submitted"))
                }
                disabled={busy === "loan" || !loanForm.amount || Number(loanForm.amount) > loanData.remaining}
                className="mt-5 w-full bg-[#D4AF37] text-[#1A1A1A] py-3.5 text-sm font-bold tracking-wide hover:bg-white transition-colors duration-300 disabled:opacity-50"
                data-testid="loan-submit-button"
              >
                {t("d_loan_apply")}
              </button>
            </>
          )}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-[#E5E2D9] p-7" data-testid="loan-requests-card">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("d_loan_requests")}</h3>
          {loanData.loans.length === 0 ? (
            <p className="text-sm text-[#5C5C5C]" data-testid="loan-empty">{t("d_loan_none")}</p>
          ) : (
            <div className="space-y-3">
              {loanData.loans.map((l) => {
                const emis = l.emis || [];
                const paidCount = emis.filter((e) => e.status === "paid").length;
                const nextEmi = emis.find((e) => e.status === "pending");
                const openEmi = emiPay && emiPay.loanId === l.id ? emiPay.n : null;
                return (
                  <div key={l.id} className="border border-[#E5E2D9] px-4 py-3" data-testid={`loan-row-${l.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-bold text-[#1A1A1A] tabular">{inr(l.amount)}</div>
                        <div className="text-xs text-[#5C5C5C] tabular mt-0.5">{t("d_emi")} {inr(l.emi)} × {l.tenure_months} {t("d_months")}</div>
                      </div>
                      <StatusPill status={l.status} />
                    </div>
                    {l.status === "approved" && emis.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs font-bold text-[#5C5C5C] mb-2">
                          <span>{t("d_emi_schedule")}</span>
                          <span data-testid={`loan-emi-progress-${l.id}`}>{paidCount}/{emis.length} {t("d_emi_progress")}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {emis.map((e) => {
                            const overdue = e.status === "pending" && e.due_date < new Date().toISOString().slice(0, 10);
                            const cls = e.status === "paid"
                              ? "bg-[#E9F7EF] text-[#089950] border-[#089950]/30"
                              : e.status === "verification"
                                ? "bg-[#FEF3E2] text-[#B5952F] border-[#D4AF37]/40"
                                : overdue
                                  ? "bg-[#E03131]/5 text-[#E03131] border-[#E03131]/30"
                                  : "bg-[#F3F1EB] text-[#5C5C5C] border-[#E5E2D9]";
                            return (
                              <span key={e.n} title={`${t("d_emi_due")}: ${e.due_date}`} className={`border text-[10px] font-bold px-2 py-1 tabular ${cls}`} data-testid={`emi-chip-${l.id}-${e.n}`}>
                                #{e.n} {e.status === "paid" ? "✓" : inr(e.amount)}
                              </span>
                            );
                          })}
                        </div>
                        {nextEmi && (
                          <div className="mt-3">
                            {openEmi === nextEmi.n ? (
                              <div className="border border-[#D4AF37] bg-[#FDFCF8] p-4" data-testid={`emi-pay-form-${l.id}`}>
                                <div className="text-sm font-bold text-[#1A1A1A] mb-3">EMI #{nextEmi.n} — {inr(nextEmi.amount)}</div>
                                <input value={emiRef} onChange={(e) => setEmiRef(e.target.value)} className={inputCls} placeholder={t("d_reference_id")} data-testid="emi-reference-input" />
                                <input ref={emiFileRef} type="file" accept="image/*" onChange={(e) => setEmiProofFile(e.target.files?.[0] || null)} className="hidden" data-testid="emi-proof-input" />
                                <button type="button" onClick={() => emiFileRef.current?.click()} className="mt-3 w-full border border-dashed border-[#D4AF37] bg-white px-4 py-2.5 text-xs font-bold text-[#5C5C5C] hover:bg-[#F7F0D8] transition-colors duration-200" data-testid="emi-proof-button">
                                  {emiProofFile ? emiProofFile.name : t("d_proof_choose")}
                                </button>
                                <div className="flex gap-2 mt-3">
                                  <button onClick={() => submitEmi(l.id, nextEmi.n)} disabled={busy === "emi" || !emiRef.trim() || !emiProofFile} className="flex-1 bg-[#1A1A1A] text-[#D4AF37] py-2.5 text-xs font-bold disabled:opacity-50" data-testid="emi-submit-button">
                                    {t("d_emi_pay")}
                                  </button>
                                  <button onClick={() => { setEmiPay(null); setEmiRef(""); setEmiProofFile(null); }} className="px-4 border border-[#E5E2D9] text-xs font-bold text-[#5C5C5C]" data-testid="emi-cancel-button">
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => { setEmiPay({ loanId: l.id, n: nextEmi.n }); setEmiRef(""); setEmiProofFile(null); }} className="w-full bg-[#1A1A1A] text-[#D4AF37] py-3 text-sm font-bold hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300" data-testid={`emi-pay-button-${l.id}`}>
                                {t("d_emi_pay")} #{nextEmi.n} — {inr(nextEmi.amount)}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      <div className="bg-white border border-[#E5E2D9] mb-12" data-testid="investments-table-card">
        <div className="px-7 py-5 border-b border-[#E5E2D9]">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("d_my_investments")}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
                <th className="px-7 py-4 font-bold">{t("d_plan")}</th>
                <th className="px-7 py-4 font-bold text-right">{t("d_amount")}</th>
                <th className="px-7 py-4 font-bold">{t("d_rate")}</th>
                <th className="px-7 py-4 font-bold">{t("d_maturity")}</th>
                <th className="px-7 py-4 font-bold text-right">{t("d_expected")}</th>
                <th className="px-7 py-4 font-bold">{t("d_status")}</th>
              </tr>
            </thead>
            <tbody>
              {investments.length === 0 && (
                <tr><td colSpan={6} className="px-7 py-8 text-center text-[#5C5C5C]" data-testid="investments-empty">{t("d_no_entries")}</td></tr>
              )}
              {investments.map((inv) => (
                <tr key={inv.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`investment-row-${inv.id}`}>
                  <td className="px-7 py-4 font-semibold text-[#1A1A1A]">{planName(inv.plan_type)}</td>
                  <td className="px-7 py-4 text-right tabular">{inr(inv.principal)}</td>
                  <td className="px-7 py-4 text-[#5C5C5C]">{inv.rate_label} · {inv.term_label}</td>
                  <td className="px-7 py-4 tabular text-[#5C5C5C]">{inv.maturity_date || "—"}</td>
                  <td className="px-7 py-4 text-right tabular font-bold text-[#089950]">{inr(inv.expected_total)}</td>
                  <td className="px-7 py-4"><StatusPill status={inv.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9]" data-testid="passbook-card">
        {chartData.length > 1 && (
          <div className="px-7 pt-6" data-testid="customer-balance-chart">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-[#1A1A1A]">{t("d_growth")}</h3>
              <span className="text-xs text-[#5C5C5C]">{t("d_growth_sub")}</span>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E5E2D9" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={false} tickLine={false} width={56}
                    tickFormatter={(v) => `₹${v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                  <Tooltip formatter={(v) => [inr(v), t("d_balance")]} contentStyle={{ border: "1px solid #E5E2D9", borderRadius: 0, fontSize: 13 }} />
                  <Area type="monotone" dataKey="balance" stroke="#B5952F" strokeWidth={2.5} fill="url(#balFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <div className="px-7 py-5 border-b border-[#E5E2D9] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("d_passbook")}</h3>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#B5952F]">{t("d_day_to_day")}</span>
          </div>
          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="inline-flex items-center gap-2 border border-[#1A1A1A] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-50"
            data-testid="passbook-pdf-button"
          >
            <Download size={14} /> {t("d_download_pdf")}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
                <th className="px-7 py-4 font-bold">{t("d_date")}</th>
                <th className="px-7 py-4 font-bold">{t("d_description")}</th>
                <th className="px-7 py-4 font-bold text-right">{t("d_credit")} (+)</th>
                <th className="px-7 py-4 font-bold text-right">{t("d_debit")} (−)</th>
                <th className="px-7 py-4 font-bold text-right">{t("d_balance")}</th>
              </tr>
            </thead>
            <tbody>
              {passbook.entries.length === 0 && (
                <tr><td colSpan={5} className="px-7 py-8 text-center text-[#5C5C5C]" data-testid="passbook-empty">{t("d_no_entries")}</td></tr>
              )}
              {passbook.entries.map((e) => (
                <tr key={e.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`passbook-row-${e.id}`}>
                  <td className="px-7 py-4 tabular text-[#5C5C5C] whitespace-nowrap">{e.entry_date}</td>
                  <td className="px-7 py-4">
                    {e.kind === "return" ? (
                      <span className="font-semibold text-[#1A1A1A]">{e.note}</span>
                    ) : (
                      <>
                        <span className="font-semibold text-[#1A1A1A]">{t(`k_${e.kind}`)}</span>
                        <span className="text-[#5C5C5C]"> — {e.note}</span>
                      </>
                    )}
                  </td>
                  <td className="px-7 py-4 text-right tabular font-bold text-[#089950]">{e.amount > 0 ? `+${inr(e.amount)}` : "—"}</td>
                  <td className="px-7 py-4 text-right tabular font-bold text-[#E03131]">{e.amount < 0 ? `−${inr(-e.amount)}` : "—"}</td>
                  <td className="px-7 py-4 text-right tabular font-bold text-[#1A1A1A]">{inr(e.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashShell>
  );
}
